import { useCallback, useEffect, useState } from 'react';

/**
 * Minimal history router.
 *
 * The app has four screens; a routing library would be more bundle than the
 * feature. `navigate` pushes, the popstate listener handles back/forward.
 */

export type Route = { path: string; query: URLSearchParams };

/**
 * The address being rendered when there is no window: the build renders every
 * public page to its own HTML file (`entry-server.tsx`) and tells the router
 * which one it is working on. In the browser this is never read.
 */
let serverPath = '/';

export function setServerPath(path: string): void {
  serverPath = path;
}

function currentRoute(): Route {
  if (typeof window === 'undefined') return { path: serverPath, query: new URLSearchParams() };
  return {
    // `/blog/` and `/blog` are the same page. A trailing slash used to make the
    // path match nothing, so a public address typed with one drew the app, and
    // a prerendered page arrived only to be replaced by the sign-in screen.
    path: window.location.pathname.replace(/(.)\/+$/, '$1'),
    query: new URLSearchParams(window.location.search),
  };
}

/**
 * Marks a history entry an overlay pushed so the back gesture closes it (the
 * phone's full-screen menu). It stands at the same address as the page under
 * it, so it is not a step worth keeping.
 */
export const OVERLAY_HISTORY_KEY = 'krevyaOverlay';

export function navigate(to: string): void {
  const state: unknown = window.history.state;
  const onOverlay = typeof state === 'object' && state !== null && OVERLAY_HISTORY_KEY in state;
  // Leaving from inside an overlay replaces its entry, so Back from the new
  // screen returns to the page the overlay was opened over, not to a copy.
  if (onOverlay) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Like `navigate`, but the address is swapped rather than added to history.
 * A search box that pushed on every keystroke would make Back walk through
 * every word the merchant typed.
 */
export function replace(to: string): void {
  window.history.replaceState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute);

  const sync = useCallback(() => setRoute(currentRoute()), []);

  useEffect(() => {
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, [sync]);

  return route;
}

/** Matches `/app/projects/:id` style paths. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (const [index, part] of patternParts.entries()) {
    const actual = pathParts[index] ?? '';
    if (part.startsWith(':')) {
      params[part.slice(1)] = decodeURIComponent(actual);
    } else if (part !== actual) {
      return null;
    }
  }
  return params;
}
